import {
  DOT,
  EMPTY,
  MAX_SIZE,
  MAX_STARS,
  REGION_IDS,
  STAR,
  type StarBattleDefinition,
  type StarBattleProgress,
  type StarBattleSubmission,
} from "./types";

const DEFAULT_RULES = {
  en: "Place stars so that every row, every column and every outlined region holds exactly the stated number of stars. Two stars never touch, not even diagonally.",
  fr: "Placez des étoiles de sorte que chaque ligne, chaque colonne et chaque région délimitée en contienne exactement le nombre indiqué. Deux étoiles ne se touchent jamais, même en diagonale.",
};

/** One region per row: valid from the start, if not much of a puzzle. */
export function stripedRegions(size: number): string[] {
  return Array.from({ length: size }, (_, r) => (REGION_IDS[r] ?? "Z").repeat(size));
}

export function emptyDefinition(): StarBattleDefinition {
  return {
    puzzleType: "star_battle",
    typeVersion: 1,
    size: 5,
    stars: 1,
    regions: stripedRegions(5),
    rulesText: { ...DEFAULT_RULES },
  };
}

export function clampSize(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(MAX_SIZE, Math.max(1, Math.trunc(value)));
}

export function clampStars(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(MAX_STARS, Math.max(1, Math.trunc(value)));
}

export function blankRows(size: number): string[] {
  return Array.from({ length: size }, () => EMPTY.repeat(size));
}

/** Crops or pads a row set to a square of the given size, keeping the top-left corner. */
export function resizeRows(source: string[], size: number): string[] {
  return Array.from({ length: size }, (_, r) => {
    const row = source[r] ?? "";
    return row.length >= size ? row.slice(0, size) : row + EMPTY.repeat(size - row.length);
  });
}

/**
 * Crops or grows the regions to a square of the given size. A new cell joins the region to
 * its left, a new row copies the one above: every cell keeps a region, and nothing new appears.
 */
export function resizeRegions(source: string[], size: number): string[] {
  const out: string[] = [];
  for (let r = 0; r < size; r++) {
    const base = (source[r] ?? out[r - 1] ?? "").replace(/[^A-Za-z0-9]/g, "");
    let row = base.slice(0, size);
    while (row.length < size) row += row[row.length - 1] ?? REGION_IDS[0];
    out.push(row);
  }
  return out;
}

/** Reads the text grid back: one line per row, spaces between cells ignored. */
export function parseRegions(text: string, size: number): string[] {
  return resizeRegions(
    text.split("\n").map((line) => line.replace(/[\s,;]+/g, "")),
    size,
  );
}

function sanitizeRow(raw: unknown, allowed: readonly string[]): string {
  if (typeof raw !== "string") return "";
  return Array.from(raw, (char) => (allowed.includes(char) ? char : EMPTY)).join("");
}

export function emptyProgress(definition: StarBattleDefinition): StarBattleProgress {
  return { version: 1, rows: blankRows(definition.size) };
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(
  definition: StarBattleDefinition,
  raw: unknown,
): StarBattleProgress {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return emptyProgress(definition);
  const source = (raw as { rows?: unknown }).rows;
  if (!Array.isArray(source)) return emptyProgress(definition);
  const rows = source.map((row) => sanitizeRow(row, [STAR, DOT, EMPTY]));
  return { version: 1, rows: resizeRows(rows, definition.size) };
}

/** The server only reads stars: dots are the player's own notes. */
export function serializeSubmission(
  definition: StarBattleDefinition,
  progress: StarBattleProgress,
): StarBattleSubmission {
  return {
    rows: resizeRows(progress.rows, definition.size).map((row) => row.replaceAll(DOT, EMPTY)),
  };
}
